// Trends block — signup activity, AI provider mix, auth funnel, and
// subscription state. Four small panels in one collapsible section so
// the dashboard isn't overwhelming when nothing's changing.

import { Sparkline } from './Sparkline';

export interface DayPoint {
  date: string;
  count: number;
}

export interface ProviderMixRow {
  provider: string;
  model: string;
  orgCount: number;
  callsLast30d: number;
}

export interface AuthFunnel {
  windowDays: number;
  signups: number;
  emailVerified: number;
  joinedOrg: number;
  verifyRate: number;
  joinRate: number;
}

export interface TrialEndingSoonRow {
  orgId: string;
  orgName: string;
  product: string;
  trialEndsAt: string;
}

export interface SubscriptionState {
  activeByProduct: Record<string, number>;
  trialsByProduct: Record<string, number>;
  trialsEndingSoon: TrialEndingSoonRow[];
  expiredOrCanceledThisWeek: number;
}

interface Props {
  signupsTrend: { orgs: DayPoint[]; members: DayPoint[]; windowDays: number };
  aiProviderMix: ProviderMixRow[];
  authFunnel: AuthFunnel;
  subscriptionState: SubscriptionState;
  env: 'dev' | 'staging' | 'prod';
}

const ENV_LABEL: Record<Props['env'], string> = {
  dev: 'Dev',
  staging: 'Staging',
  prod: 'Prod',
};

export function TrendsBlock({
  signupsTrend,
  aiProviderMix,
  authFunnel,
  subscriptionState,
  env,
}: Props) {
  return (
    <section className="border border-slate-800 rounded-lg overflow-hidden">
      <div className="bg-slate-900/40 px-4 py-2.5 border-b border-slate-800">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
          Activity & Trends ({ENV_LABEL[env]})
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-800">
        <SignupsPanel signupsTrend={signupsTrend} />
        <AuthFunnelPanel funnel={authFunnel} />
        <ProviderMixPanel rows={aiProviderMix} />
        <SubscriptionStatePanel state={subscriptionState} />
      </div>
    </section>
  );
}

// ── panels ──────────────────────────────────────────────────────────────

function SignupsPanel({
  signupsTrend,
}: {
  signupsTrend: Props['signupsTrend'];
}) {
  const orgsTotal = signupsTrend.orgs.reduce((a, b) => a + b.count, 0);
  const membersTotal = signupsTrend.members.reduce((a, b) => a + b.count, 0);
  return (
    <div className="bg-slate-950/40 p-4">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">
        Signups · last {signupsTrend.windowDays}d
      </div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-2xl font-black text-slate-100">{orgsTotal}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">new orgs</div>
        </div>
        <Sparkline
          data={signupsTrend.orgs.map((p) => p.count)}
          width={120}
          height={28}
          color="#a5b4fc"
          fill="rgba(165,180,252,0.10)"
        />
      </div>
      <div className="flex items-center justify-between gap-3 mt-2">
        <div>
          <div className="text-lg font-bold text-slate-200">{membersTotal}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">new members</div>
        </div>
        <Sparkline
          data={signupsTrend.members.map((p) => p.count)}
          width={120}
          height={20}
          color="#67e8f9"
        />
      </div>
    </div>
  );
}

function AuthFunnelPanel({ funnel }: { funnel: AuthFunnel }) {
  const rows: Array<{ label: string; count: number; pct: number; tone: string }> = [
    { label: 'Signups', count: funnel.signups, pct: 1, tone: 'bg-indigo-500/40' },
    {
      label: 'Email verified',
      count: funnel.emailVerified,
      pct: funnel.signups > 0 ? funnel.emailVerified / funnel.signups : 0,
      tone: 'bg-sky-500/40',
    },
    {
      label: 'Joined org',
      count: funnel.joinedOrg,
      pct: funnel.signups > 0 ? funnel.joinedOrg / funnel.signups : 0,
      tone: 'bg-emerald-500/40',
    },
  ];
  return (
    <div className="bg-slate-950/40 p-4">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">
        Auth funnel · last {funnel.windowDays}d
      </div>
      <ul className="space-y-2 text-xs">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center gap-2">
            <span className="w-24 text-[10px] uppercase tracking-wider text-slate-400">
              {r.label}
            </span>
            <div className="flex-1 h-3 bg-slate-800 rounded overflow-hidden">
              <div
                className={`h-full ${r.tone} transition-all`}
                style={{ width: `${Math.max(2, Math.round(r.pct * 100))}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-slate-300 w-16 text-right">
              {r.count} ({Math.round(r.pct * 100)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProviderMixPanel({ rows }: { rows: ProviderMixRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="bg-slate-950/40 p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">
          AI provider mix
        </div>
        <div className="text-xs text-slate-500 italic">No org has a default provider configured.</div>
      </div>
    );
  }
  const maxOrgCount = Math.max(...rows.map((r) => r.orgCount), 1);
  return (
    <div className="bg-slate-950/40 p-4">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">
        AI provider mix · default configs &amp; calls / 30d
      </div>
      <ul className="space-y-1.5 text-xs">
        {rows.slice(0, 6).map((r) => (
          <li key={`${r.provider}::${r.model}`} className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 w-16 flex-shrink-0">
              {r.provider}
            </span>
            <span className="text-[11px] text-slate-300 font-mono truncate flex-1 min-w-0">
              {r.model}
            </span>
            <div className="w-20 h-2 bg-slate-800 rounded overflow-hidden">
              <div
                className="h-full bg-indigo-500/50 transition-all"
                style={{ width: `${Math.max(4, Math.round((r.orgCount / maxOrgCount) * 100))}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-slate-400 w-12 text-right">
              {r.orgCount} org · {r.callsLast30d} ev
            </span>
          </li>
        ))}
        {rows.length > 6 && (
          <li className="text-[10px] text-slate-500 italic">…and {rows.length - 6} more</li>
        )}
      </ul>
    </div>
  );
}

function SubscriptionStatePanel({ state }: { state: SubscriptionState }) {
  const activeRows = Object.entries(state.activeByProduct);
  const trialRows = Object.entries(state.trialsByProduct);
  return (
    <div className="bg-slate-950/40 p-4">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">
        Subscriptions
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Active</div>
          {activeRows.length === 0 ? (
            <div className="text-slate-500 italic text-[11px]">none</div>
          ) : (
            <ul className="space-y-0.5">
              {activeRows.map(([product, count]) => (
                <li key={product} className="text-slate-200">
                  <span className="font-mono text-[11px]">{product.replace('persona_', '')}</span>{' '}
                  <span className="text-emerald-400 font-bold">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Trial</div>
          {trialRows.length === 0 ? (
            <div className="text-slate-500 italic text-[11px]">none</div>
          ) : (
            <ul className="space-y-0.5">
              {trialRows.map(([product, count]) => (
                <li key={product} className="text-slate-200">
                  <span className="font-mono text-[11px]">{product.replace('persona_', '')}</span>{' '}
                  <span className="text-amber-300 font-bold">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {state.trialsEndingSoon.length > 0 && (
        <div className="border-t border-slate-800 pt-2 mt-2">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-1">
            Trials ending ≤ 14d ({state.trialsEndingSoon.length})
          </div>
          <ul className="space-y-0.5 text-[11px]">
            {state.trialsEndingSoon.slice(0, 4).map((t) => (
              <li
                key={`${t.orgId}:${t.product}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate text-slate-300">{t.orgName}</span>
                <span className="text-[10px] font-mono text-slate-500 whitespace-nowrap">
                  {t.product.replace('persona_', '')} · {formatRel(t.trialEndsAt)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {state.expiredOrCanceledThisWeek > 0 && (
        <div className="text-[10px] text-rose-300 mt-2">
          {state.expiredOrCanceledThisWeek} expired or canceled in last 7d
        </div>
      )}
    </div>
  );
}

function formatRel(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = then - now;
    const days = Math.round(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return `${Math.abs(days)}d ago`;
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    return `in ${days}d`;
  } catch {
    return iso;
  }
}
